// @ts-strict-ignore
import { useCallback } from "react";

import type { To } from "@shared/react-router-dom/react-router-dom";
import { useNavigate, useLocation } from "@shared/react-router-dom/react-router-dom";
import { isRemix } from "@shared/utils/fullscriptEnv/fullscriptEnv";

interface Options {
  /** The pathname to navigate to. Defaults to current pathname */
  pathname?: string;
  /** Whether to replace the current history entry. Defaults to false */
  replace?: boolean;
  /**
   * Whether to preserve scroll position during navigation.
   * Uses React-Router/Remix's built-in scroll handling.
   * @default false
   */
  preserveScroll?: boolean;
}

const ARRAY_SUFFIX_INDICATOR = "_array";

const getParamsString = <T,>(paramObject: T): string => {
  const urlParams = new URLSearchParams();
  const filteredParamsEntries = Object.entries(paramObject).filter(entry => entry[1] !== undefined);

  filteredParamsEntries.forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach(subValue => {
        urlParams.append(key + ARRAY_SUFFIX_INDICATOR, JSON.stringify(subValue));
      });
    } else {
      urlParams.append(key, JSON.stringify(value));
    }
  });

  return urlParams.toString();
};

const tryParse = value => {
  try {
    return JSON.parse(value);
  } catch {
    // In case it's just a string and not in JSON format
    return value;
  }
};

const parseParamsString = <T,>(paramString: string): T => {
  const urlParams = new URLSearchParams(paramString);
  const paramObject = {};

  const isArrayParam = (key: string) => key.endsWith(ARRAY_SUFFIX_INDICATOR);
  const removeArrayIndicator = (key: string) =>
    key.slice(0, key.length - ARRAY_SUFFIX_INDICATOR.length);

  urlParams.forEach((value, key) => {
    if (isArrayParam(key)) {
      const parsedKey = removeArrayIndicator(key);
      const currentArray = paramObject[parsedKey] || [];

      paramObject[parsedKey] = [...currentArray, tryParse(value)];
    } else if (key in paramObject && !Array.isArray(paramObject[key])) {
      paramObject[key] = [paramObject[key], tryParse(value)];
    } else if (key in paramObject) {
      paramObject[key] = [...paramObject[key], tryParse(value)];
    } else {
      paramObject[key] = tryParse(value);
    }
  });

  return paramObject as T;
};

const useUrlParams = <T extends Record<string, any>>(
  initialOptions: Options = {}
): [T, (params: T, options?: Options) => void] => {
  const navigate = useNavigate();
  const location = useLocation();
  const searchParams = location.search;
  const locPathname = location.pathname;

  const setUrlParams = useCallback(
    (params: T, options?: Options): void => {
      const defaultOptions = {
        pathname: locPathname,
        replace: false,
        preserveScroll: false,
      };

      const { pathname, replace, preserveScroll } = {
        ...defaultOptions,
        ...initialOptions,
        ...options,
      };

      const newLocation: To = {
        pathname,
        search: getParamsString<T>(params),
      };

      const getNavigateOptions = () => {
        if (!preserveScroll) return;

        if (isRemix()) {
          // support for remix's default ScrollRestoration component
          return { preventScrollReset: true };
        }

        // support for our custom ManualScrollRestoration component
        return { state: { scrollPosition: window.scrollY } };
      };

      const navOptions = getNavigateOptions();
      navigate(newLocation, { replace, ...navOptions });
    },
    [navigate, locPathname, initialOptions]
  );

  return [parseParamsString<T>(searchParams), setUrlParams];
};

export type { Options as ParamsOptions };
export { useUrlParams, parseParamsString, getParamsString };
